// ============================================================
// MongoDBCrudUpdate.jsx
//
// A branded, technical guide to updating data in MongoDB.
// Covers updateOne, updateMany, and Atomic Operators.
//
// Brand palette:
//   Background  → #001E2B  (MongoDB Deep Navy)
//   Primary     → #00ED64  (MongoDB Spring Green)
//   Accent      → #F9D71C  (Yellow / Warning & Attention)
//   Text        → #FFFFFF  (White)
//   Code        → #010409  (GitHub Dark)
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Stack,
    IconButton,
    Avatar,
    Alert,
    Button
} from "@mui/material";

/* MUI Icons */
import EditIcon from "@mui/icons-material/Edit";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import TerminalIcon from "@mui/icons-material/Terminal";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import BoltIcon from "@mui/icons-material/Bolt";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#001E2B",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(0, 237, 100, 0.2)",
    borderHover: "rgba(0, 237, 100, 0.6)",
    green: "#00ED64",
    yellow: "#F9D71C",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    codeBg: "#010409",
};

/* ── Sub-Components ──────────────────────────────────────── */

function CodeTerminal({ code, label, color = BRAND.green }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.02)" }}>
                <Typography variant="caption" sx={{ color: color, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ p: 2.5, overflowX: "auto" }}>
                <Typography component="pre" sx={{ fontFamily: "'Fira Code', monospace", fontSize: 13, color: "#D1D5DB", lineHeight: 1.5 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

/* ── Main Component ──────────────────────────────────────── */

export default function MongoDBCrudUpdate() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        icon={<SchoolIcon style={{ color: BRAND.green }} />}
                        label="CRUD Operations · Module 07"
                        sx={{ bgcolor: "rgba(0,237,100,0.1)", color: BRAND.green, border: `1px solid ${BRAND.border}`, mb: 2 }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Update <span style={{ color: BRAND.green }}>Operations</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                        Modifying data in MongoDB is efficient and atomic. Use specialized operators to change
                        specific fields without overwriting the entire document.
                    </Typography>
                </Box>

                {/* 2. THE $SET OPERATOR */}
                <Grid container spacing={6} alignItems="center" sx={{ mb: 10 }}>
                    <Grid item xs={12} md={5}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                            <EditIcon sx={{ color: BRAND.green, fontSize: 32 }} />
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>updateOne() & $set</Typography>
                        </Box>
                        <Typography sx={{ color: BRAND.muted, mb: 3, lineHeight: 1.8 }}>
                            The <code>$set</code> operator replaces the value of a field with the specified value.
                            If the field does not exist, <code>$set</code> will add a new field with the specified value.
                        </Typography>
                        <Alert severity="warning" sx={{ bgcolor: "rgba(249,215,28,0.05)", color: BRAND.white, border: `1px solid ${BRAND.yellow}` }}>
                            <Typography variant="body2">
                                <strong>Important:</strong> If you don't use <code>$set</code> in an <code>update()</code> call,
                                you might replace the <strong>entire document</strong>!
                            </Typography>
                        </Alert>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <CodeTerminal
                            label="mongosh: Basic Update"
                            code={`db.users.updateOne(\n  { _id: ObjectId("65f1a...") },\n  { $set: { status: "active", verified: true } }\n);`}
                        />
                    </Grid>
                </Grid>



                <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mb: 10 }} />

                {/* 3. ATOMIC OPERATORS */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <BoltIcon sx={{ color: BRAND.green, fontSize: 32 }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Powerful Atomic Operators</Typography>
                    </Box>
                    <Grid container spacing={3}>
                        {[
                            { op: "$inc", desc: "Increments a field by a specified value.", example: "{ $inc: { loginCount: 1 } }" },
                            { op: "$push", desc: "Adds an item to an array field.", example: "{ $push: { tags: 'new-tag' } }" },
                            { op: "$rename", desc: "Renames a field instantly.", example: "{ $rename: { 'oldName': 'newName' } }" },
                            { op: "$unset", desc: "Removes a specific field from a doc.", example: "{ $unset: { bio: '' } }" }
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} md={3} key={i}>
                                <Paper sx={{ p: 2.5, bgcolor: BRAND.surface, borderRadius: 3, border: `1px solid ${BRAND.border}`, height: '100%' }}>
                                    <Typography variant="h6" sx={{ color: BRAND.green, fontWeight: 800 }}>{item.op}</Typography>
                                    <Typography variant="body2" sx={{ color: BRAND.muted, mb: 1 }}>{item.desc}</Typography>
                                    <Typography variant="caption" sx={{ fontFamily: 'monospace', color: BRAND.white }}>{item.example}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* 4. UPSERT & MULTI-UPDATE */}
                <Grid container spacing={6} alignItems="center" sx={{ mb: 10 }}>
                    <Grid item xs={12} md={7} order={{ xs: 2, md: 1 }}>
                        <CodeTerminal
                            label="mongosh: Bulk & Upsert"
                            code={`// Update many documents at once\ndb.users.updateMany(\n  { age: { $lt: 18 } },\n  { $set: { category: "minor" } }\n);\n\n// UPSERT: Insert if not found\ndb.users.updateOne(\n  { email: "new@user.com" },\n  { $set: { name: "New User" } },\n  { upsert: true }\n);`}
                        />
                    </Grid>
                    <Grid item xs={12} md={5} order={{ xs: 1, md: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                            <UpgradeIcon sx={{ color: BRAND.green, fontSize: 32 }} />
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>Upserts & Bulk Updates</Typography>
                        </Box>
                        <Typography sx={{ color: BRAND.muted, mb: 3, lineHeight: 1.8 }}>
                            An <strong>Upsert</strong> (Update + Insert) is a powerful pattern. If no document matches
                            the filter, a new one is created using the filter and update criteria.
                            Use <code>updateMany()</code> to apply changes to an entire subset of data.
                        </Typography>
                    </Grid>
                </Grid>



                <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mb: 10 }} />

                {/* 5. PRO-TIP: RETURNING DATA */}
                <Box sx={{ mb: 10 }}>
                    <Paper sx={{ p: 3, bgcolor: "rgba(255,255,255,0.02)", border: `1px solid ${BRAND.border}`, borderRadius: 4 }}>
                        <Stack direction="row" spacing={2}>
                            <Avatar sx={{ bgcolor: BRAND.green }}><TipsAndUpdatesIcon /></Avatar>
                            <Box>
                                <Typography variant="h6" sx={{ color: BRAND.white, fontWeight: 700 }}>Find And Modify</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted, mt: 1 }}>
                                    Standard update methods return an acknowledgment. If you need the <strong>actual document</strong> returned
                                    after the update, use <code>findOneAndUpdate()</code>. In Mongoose, remember to pass
                                    <code>{"{ new: true }"}</code> to get the modified version!
                                </Typography>
                            </Box>
                        </Stack>
                    </Paper>
                </Box>

                {/* FOOTER CTA */}
                <Box sx={{
                    p: 8, borderRadius: 8, textAlign: "center",
                    background: `linear-gradient(45deg, ${BRAND.bg}, #00684A)`,
                    border: `1px solid ${BRAND.border}`,
                    boxShadow: `0 20px 40px rgba(0,0,0,0.5)`
                }}>
                    <PublishedWithChangesIcon style={{ fontSize: 40, color: BRAND.green, marginBottom: 16 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Data Synchronized?</Typography>
                    <Typography sx={{ color: BRAND.muted, mb: 4 }}>
                        Updating data correctly ensures system integrity. Next, we will cover how to safely
                        remove data using Delete operations.
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button variant="contained" sx={{ bgcolor: BRAND.green, color: BRAND.bg, fontWeight: 900 }}>Go to Delete Ops</Button>
                        <Button variant="outlined" sx={{ color: BRAND.white, borderColor: BRAND.muted }}>View Operator Docs</Button>
                    </Stack>
                </Box>

            </Container>
        </Box>
    );
}
